<template>
  <div class="gs-app gm-mail-segments-refresh gm-refresh-tables flex mb-4 mx-4">
    <!-- Loader -->
    <portal to="loaderTarget">
      <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
        <gd-loader :label="$t('frontend_loading')"/>
      </div>
    </portal>

    <div class="w-full gm-mail-segments-refresh__workspace">
      <navigation-tabs
        class="gm-nav-segment-tabs gm-nav-segment-tabs--in-flow"
        :nav_tabs="navTabs"
      ></navigation-tabs>
      <server-table
        class="text-center groovemail-segment-table gs-refresh-controls"
        :url="segmentsUrl"
        ref="serverTable"
        tableRef="segmentsTable"
        :slotData="segmentTableSlotData"
        :tableColumns="segmentsTableColumns"
        :tableOptions="segmentsTableOptions"
        :emptyStateData="emptyStateData"
        tableTitle="frontend_groovemail_segment_route_page_title"
        addNewBtnText="frontend_groovemail_segment_new_segment"
        @addNewData="openCreateMaillead"
        @loaded="hideLoader()"
        @loading="showLoader()"
      >
      </server-table>
      <gs-named-modal
        name="mail-segment-modal"
        :title="modalData.title"
        @closed="modalClosed"
      >
        <div class="gs-app p-6 gm-mail-segments-refresh__dialog gm-refresh-dialog">
          <div class="flex flex-col mb-6">
            <label class="font-semibold text-sm text-grey-700 mb-2">
              {{ $t('frontend_groovemail_mail_tag_name') }}
            </label>
            <text-input
              variant="base"
              v-model="model.name"
              :placeholder="$t('frontend_groovemail_adminsetting_name')"
              :validator="$v.model.name"
            />
          </div>
          <div class="flex justify-end">
            <gs-button
              variant="outline"
              @click="closeMailSegmentModal()"
              >{{ $t('frontend_cancel') }}</gs-button>
            <gs-button
              class="ml-2"
              variant="primary"
              @click="modalData.save()"
            >
              {{ modalData.btnText }}
            </gs-button>
          </div>
        </div>
      </gs-named-modal>
    </div>

    <!-- Confirmation delete modal -->
    <confirm-modal
      :ref="`confirmation-delete-mail-segment`"
      :name="`confirmation-delete-mail-segment`"
      :buttonText="$t('frontend_confirm')"
      :cancelText="$t('frontend_cancel')"
      :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
      :messageContent="this.$t('frontend_groovemail_swal_revert_message')"
      @confirm="deleteSegment"
      @close="closeDeleteSegmentModal"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import ServerTable from '@groovepages/gd-ui-kit/components/tables/ServerTable.vue';
import NavigationTabs from '@/components/navigation-tabs.vue';
import { segmentUrls } from '@/utils/urls.js';
import { getData, deleteData, postData, putData } from '@/utils/apiHelper.js';
import themeConfig from '@/theme/themeConfig';
import showAlert from '@/components/sweet-alert-popup.js';

export default {
  middleware: 'auth',
  metaInfo() {
    return { title: this.$t('frontend_groovemail_segment_title') };
  },
  data() {
    return {
      modalType: 'add',
      segmentsUrl: segmentUrls.segment,
      mailSegmentId: null,
      isLoading: false,
      model: {
        name: '',
        includeType: 'AND',
        includeConditions: [],
        excludeType: 'AND',
        excludeConditions: [],
      },
      navTabs: [
        {
          tabName: 'frontend_groovemail_components_pricepoint_sidebar_lists',
          tabRoute: 'Lists',
        },
        {
          tabName: 'frontend_groovemail_components_pricepoint_sidebar_tags',
          tabRoute: 'Tags',
        },
        {
          tabName: 'frontend_sites_template_categories',
          tabRoute: 'Category',
        },
        {
          tabName: 'frontend_groovemail_components_pricepoint_sidebar_segments',
          tabRoute: 'Segments',
        },
      ],
      segmentsTableColumns: [
        'name',
        'totalleads',
        'filledemails',
        'blocked',
        'totalsend',
        'actions',
      ],
      segmentsTableOptions: {
        perPage: 10,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        pagination: { chunk: 5 },
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        texts: {
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query'),
        },
        sortable: ['name'],
        filterable: ['name'],
        headings: {
          name: () => this.$t('frontend_groovemail_segment_listing_segment_name'),
          totalleads: () => this.$t('frontend_groovemail_segment_subscribe_leads'),
          filledemails: () => this.$t('frontend_groovemail_segment_subscribe_emails'),
          blocked: () => this.$t('frontend_groovemail_segment_subscribe_blocked'),
          totalsend: () => this.$t('frontend_groovemail_segment_subscribe_total'),
          actions: () => this.$t('frontend_groovemail_mail_lead_actions'),
        },
        requestFunction(data) {
          const paramsData = { ...data };
          paramsData.ascending = paramsData.ascending === 1 ? 'asc' : 'desc';
          return axios.get(this.url, { params: paramsData }).catch((e) => {
            this.dispatch('error', e);
            this.hideLoader();
          });
        },
        responseAdapter({ data }) {
          return {
            data: data.data,
            count: data.total,
          };
        },
      },
      emptyStateData: {
        emptyStateHeading: 'frontend_groovemail_segment_route_page_title',
        emptyStateSubHeading: 'frontend_groovemail_segment_have_no_data',
        emptyStateBtnText: 'frontend_groovemail_segment_new_segment',
      },
    };
  },

  validations: {
    model: {
      name: { required, minLength: minLength(3), maxLength: maxLength(50) },
    },
  },

  components: {
    NavigationTabs,
    ServerTable,
  },

  methods: {
    // show loader
    showLoader() {
      if(!this.isLoading) {
        this.isLoading = true;
      }
    },
    
    // hide loader
    hideLoader (){
      if(this.isLoading) {
        this.isLoading = false;
      }
    },

    // error message for the request field
    async requestErrorAlert(error) {
      // hide the loader
      this.hideLoader();
      
      await showAlert({
        iconType: 'alert-error',
        html : `
          <style>.swal2-cancel { color: var(--brand-primary-invert) !important; }</style>
          <p class="text-sm text-deep-dark mb-3">${error || this.$t('frontend_backend_error') }</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },

    // Resets modal name & error message after validation when mail-segment-modal is closed.
    modalClosed() {
      this.model.name = '';
      this.$v.model.name.$reset();
    },
    
    // refreshes segment table data (i.e., Selected, Active, Inactive & In the segment count) for each row.
    refreshSegmentData() {
      const segmentData = this.$refs.serverTable.$refs.segmentsTable.data;
      try {
        // show loader
        this.showLoader();

        // refresh segment data
        for (let i = 0; i < segmentData.length; i += 1) {
          const { id } = segmentData[i];
          getData(`${segmentUrls.refreshSegment}/${id}`, null, true)
            .then((response) => {
              const segmentsStatsData = response && response.data;
              segmentData[i].totalleads = segmentsStatsData.totalleads;
              segmentData[i].filledemails = segmentsStatsData.filledemails;
              segmentData[i].blocked = segmentsStatsData.blocked;
              segmentData[i].totalsend = segmentsStatsData.totalsend;
            });
        }

        // hide loader
        // this.hideLoader();
      } catch (error) {
        this.requestErrorAlert(error.response?.data?.error);
      }

    },

    /**
     * Opens Contacts page that displays details of all contacts present in that segment.
     * @param {object} segment - contains detail of a segment.
     */
    async goToFilteredLeads(segment) {
      this.$router.push({
        name: 'Leads2',
        query: {
          segmentId: segment.id,
          segmentName: segment.name,
        },
      });
    },

    /**
     * Opens Edit Segment page which allows us to include or exclude contacts from a segment.
     * @param {string} id unique id of segment.
     */
    subscribe(Id) {
      this.$router.push({ name: 'EditSegement', params: { id: Id } });
    },

    // Opens mail segment modal that is used to perform add, clone or rename actions.
    openMailSegmentModal() {
      this.$root.$emit('modal-open', 'mail-segment-modal');
    },

    // Closes mail segment modal that is used to perform add, clone or rename actions.
    closeMailSegmentModal() {
      this.$root.$emit('modal-close', 'mail-segment-modal');
    },

    openCreateMaillead() {
      this.modalType = 'add';
      this.openMailSegmentModal();
      this.model.name = '';
      this.$v.model.name.$reset();
    },

    async openEditSegment(id) {
      this.modalType = 'rename';

      try {
        const response = await getData(`${segmentUrls.segment}/${id}`);
        this.mailsegment = response?.data;
        this.model.name = this.mailsegment.name;
        this.model.includeType =
          this.mailsegment.includeType != null
            ? this.mailsegment.includeType
            : 'AND';
        this.model.includeConditions =
          this.mailsegment.includeConditions !== '' &&
          this.mailsegment.includeConditions != null
            ? JSON.parse(this.mailsegment.includeConditions)
            : [];
        this.model.excludeType =
          this.mailsegment.excludeType != null
            ? this.mailsegment.excludeType
            : 'AND';
        this.model.excludeConditions =
          this.mailsegment.excludeConditions !== '' &&
          this.mailsegment.excludeConditions != null
            ? JSON.parse(this.mailsegment.excludeConditions)
            : [];
        this.openMailSegmentModal();

      } catch (error) {
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    // Renames name of the segment.
    async updateSegment() {
      this.$v.model.name.$touch();
      if (!this.$v.model.name.$invalid) {
        try {
          this.mailsegment.name = this.model.name;
          await putData(`${segmentUrls.segment}/${this.mailsegment.id}`, this.mailsegment);
          await this.fetchSegments();
          this.closeMailSegmentModal();
        } catch (error) {
          // show error message
          this.requestErrorAlert(error.response?.data?.error);
        }
      }
    },

    // Creates a new segment.
    async createMailSegment() {
      this.$v.model.name.$touch();

      if (!this.$v.model.name.$invalid) {
        // show loader
        this.showLoader();

        this.mailsegment = {
          name: this.model.name
        };

        try {
          const response = await postData(segmentUrls.segment, this.mailsegment);
          this.$router.push({
            name: 'CreateSegement',
            params: { id: response?.data?.id },
          });

          this.closeMailSegmentModal();
          // hide loader
          this.hideLoader();
        } catch (error) {
          this.requestErrorAlert(error.response?.data?.error);
        }
      }
    },

    // Used to refresh segment table.
    async fetchSegments() {
      // refresh segment table
      await this.$refs.serverTable.$refs.segmentsTable.refresh();
      await this.refreshSegmentData();
    },

    // open delete Segment modal
    async openDeleteSegmentModal(id) {
      // set the Segment id
      this.mailSegmentId = id;

      // open the modal
      this.$refs[`confirmation-delete-mail-segment`].open();
    },

    // close delete Segment modal
    closeDeleteSegmentModal() {
      // reset the Segment id
      this.mailSegmentId = null;

      // close the modal
      this.$refs[`confirmation-delete-mail-segment`].close();
    },

    // delete mail segment
    async deleteSegment() {
      try {
        await deleteData(`${segmentUrls.segment}/${this.mailSegmentId}`);
        await this.$refs.serverTable.$refs.segmentsTable.refresh();

        // check if the table data = 1
        if (this.$refs.serverTable.$refs.segmentsTable.data.length < 1) {
          this.tabelKey += 1;
        }
      } catch (error) {
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    async cloneSegment(segmentId) {
      this.modalType = 'clone';

      try {
        // get the clone segment details
        const response = await getData(`${segmentUrls.segment}/${segmentId}`);
        
        // set the segment details
        this.mailsegment = response?.data;
        this.model.name = `${this.mailsegment.name} [ ${this.$t('frontend_copy').toUpperCase()} ]`;
        this.mailsegment.exclude_conditions = this.mailsegment.exclude_conditions;
        this.mailsegment.include_conditions = this.mailsegment.include_conditions;

        // open the mail segment modal
        this.openMailSegmentModal();
      } catch (error) {
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    // Used to clone a segment.
    async cloneMailSegment() {
      this.$v.model.name.$touch();
      if (!this.$v.model.name.$invalid) {
        // show loader
        this.showLoader();

        try {
          const mailSegment = {
            activeleads_data: this.mailsegment.activeleads_data,
            exclude_conditions: this.mailsegment.exclude_conditions,
            include_conditions: this.mailsegment.include_conditions,
            exclude_type: this.mailsegment.excludeType,
            inactiveleads_data: this.mailsegment.inactiveleads_data,
            include_type: this.mailsegment.includeType,
            name: this.model.name,
            selected_leads_data: this.mailsegment.selected_leads_data,
            user_id: this.mailsegment.user_id,
          };
          
          // clone the segment
          const response = await postData(segmentUrls.segment, mailSegment);

          // close the mail segment modal
          this.closeMailSegmentModal();

          // redirect to the edit segment page
          this.$router.push({
            name: 'EditSegement',
            params: { id: response?.data?.id }
            
          });
          
          // hide loader
          this.hideLoader();
        } catch (error) {
          this.requestErrorAlert(error.response?.data?.error);
        }
      }
    },
  },

  computed: {
    /**
     * Used to set modal title, submit button text & method to be called based on modal type ( i.e., clone/add/remove).
     * @return {object} modal - contains modal title, modal submit button text &
     *                          a method that needs to be called when button is clicked.
     */
    modalData() {
      const modal = {};
      if (this.modalType === 'clone') {
        modal.title = this.$t('frontend_groovemail_segment_clone_segment');
        modal.btnText = this.$t('frontend_clone');
        modal.save = () => this.cloneMailSegment();
      } else if (this.modalType === 'add') {
        modal.title = this.$t('frontend_groovemail_segment_create_new_segment');
        modal.btnText = this.$t('frontend_groovemail_segment_create_segment');
        modal.save = () => this.createMailSegment();
      } else {
        modal.title = this.$t('frontend_groovemail_segment_rename_segment');
        modal.btnText = this.$t('frontend_builder_pages_rename');
        modal.save = () => this.updateSegment();
      }
      return modal;
    },
    
    /**	
     * Returns slot data for segments table.	
     * @return {array} details for slot columns for segments table.	
    */	
    segmentTableSlotData() {
      return [
        {
          slotName: 'name',
          slotType: 'computed',
          classes: () => '',
          value: (props) => props.row.name
              ? props.row.name
              : this.$t('frontend_groovemail_not_available'),
        },
        {
          slotName: 'totalleads',
          slotType: 'clickable',
          classes: () => '',
          callback: (props) => this.goToFilteredLeads(props.row),
          defaultValue: 0,
        },
        {
          slotName: 'filledemails',
          slotType: 'computed',
          classes: () => '',
          value: (props) => props.row.filledemails ? props.row.filledemails : 0,
        },
        {
          slotName: 'blocked',
          slotType: 'computed',
          classes: () => '',
          value: (props) => props.row.blocked ? props.row.blocked : 0,
        },
        {
          slotName: 'totalsend',
          slotType: 'computed',
          classes: () => 'pl-2',
          value: (props) => props.row.totalsend ? props.row.totalsend : 0,
        },
        {
          slotName: 'actions',
          slotType: 'actions',
          conditions: () => true,
          classes: () => {
            return {
              outerDiv: '',
              dropdown: '',
              outerSpan: '',
            };
          },
          actionList: [
            {
              id: 0,
              name: () => this.$t('frontend_edit'),
              conditions: () => true,
              callback: (props) => this.subscribe(props.row.id),
            },
            {
              id: 1,
              name: () => this.$t('frontend_clone'),
              conditions: () => true,
              callback: (props) => this.cloneSegment(props.row.id),
            },
            {
              id: 2,
              name: () => this.$t('frontend_builder_pages_rename'),
              conditions: () => true,
              callback: (props) => this.openEditSegment(props.row.id),
            },
            {
              id: 3,
              name: () => this.$t('frontend_delete'),
              conditions: () => true,
              callback: (props) => this.openDeleteSegmentModal(props.row.id),
            },
          ],
        },
      ];
    },
  },
};
</script>

<style lang="scss">
.groovemail-segment-table {
  .VueTables--server .table-responsive {
    th:nth-child(1),
    td:nth-child(1) {
      width: 17%;
      padding-left: 20px !important;
    }
    th:nth-child(2),
    td:nth-child(2),
    th:nth-child(3),
    td:nth-child(3),
    th:nth-child(4),
    td:nth-child(4) {
      width: 9%;
    }
    th:nth-child(5),
    td:nth-child(5) {
      width: 10%;
    }
    th:nth-child(6),
    td:nth-child(6) {
      width: 8%;
    }
    th:nth-child(7),
    td:nth-child(7) {
      width: 23%;
      padding-left: 5px !important;
      padding-right: 5px !important;
    }
    th:nth-child(8),
    td:nth-child(8) {
      width: 10%;
      padding-left: 5px !important;
      padding-right: 5px !important;
    }
  }
}

.gm-nav-segment-tabs {
  top: 37px;
  z-index: 10;
}
</style>

<style scoped>
.gm-mail-segments-refresh {
  min-height: 100%;
  padding: 28px;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gm-mail-segments-refresh__workspace {
  min-width: 0;
  padding: 20px 24px 24px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

@media (max-width: 640px) {
  .gm-mail-segments-refresh {
    padding: 18px 12px 32px;
  }

  .gm-mail-segments-refresh__workspace {
    padding: 16px;
  }
}
</style>

<style scoped>
.gm-nav-segment-tabs--in-flow { position: static; margin-bottom: 20px; }
</style>
